import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ISharedModalBasic} from '../../../../../shared-models/modals/shared-modal-basic';
import {IconColourEnum} from '../../../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {SharedModalBasicComponent} from '../../../shared-modal-basic/shared-modal-basic.component';
import {IconsEnum} from '../../../../../../shared-modules/shared-icons/models/icons.enum';
import {ModalControllerService} from '../../../../../shared-services/overlay/modal-controller.service';
import {ILayout} from '../../models/label-interfaces';

@Component({
  selector: 'app-layout-dialog',
  templateUrl: './layout-dialog.component.html',
  styleUrls: ['./layout-dialog.component.scss'],
  standalone: false,
})
export class LayoutDialogComponent implements OnInit {

  @Input() savedLayouts: ILayout[] = [];
  @Input() currentLayoutName = '';
  @Input() allowCopy = true;
  @Input() hasClipboardLayouts = false;
  @Input() clipboardCount = 0;
  @Input() isPasting = false;

  newLayoutName = '';

  @Output() readonly saveLayout = new EventEmitter<string>();
  @Output() readonly loadLayout = new EventEmitter<string>();
  @Output() readonly deleteLayout = new EventEmitter<{name: string; event: Event}>();
  @Output() readonly dialogClosed = new EventEmitter<void>();
  @Output() readonly copyLayouts = new EventEmitter<string[]>();
  @Output() readonly pasteLayouts = new EventEmitter<void>();

  selected = false;
  private selectedForCopy = new Set<string>();

  protected readonly iconColourEnum = IconColourEnum;
  readonly icons = IconsEnum;

  constructor(private modalController: ModalControllerService) {
  }

  get getNumberOfSavedDocuments(): number {
    return this.savedLayouts?.length || 0;
  }

  get canCopy(): boolean {
    return this.allowCopy && this.selectedForCopy.size > 0;
  }

  get allSelected(): boolean {
    return this.getNumberOfSavedDocuments > 0 && this.selectedForCopy.size === this.getNumberOfSavedDocuments;
  }

  ngOnInit(): void {
    this.newLayoutName = this.currentLayoutName;
  }

  getDisplayDate(layout: ILayout): string {
    if (layout && layout.timestamp) {
      const date = new Date(layout.timestamp);
      return date.toLocaleDateString('en-GB',
        {day: '2-digit', month: '2-digit', year: 'numeric'}) + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return '';
  }

  async confirmSave(): Promise<void> {
    if (!this.newLayoutName.trim()) {
      const componentProps: ISharedModalBasic = {
        buttonAccept: false,
        buttonAcceptText: '',
        buttonClose: true,
        buttonCloseCross: false,
        buttonCloseText: 'Close',
        modalTitle: 'Missing Layout Name',
        modalTitleIcon: IconsEnum.infoSlabCircleOutline,
        modalTitleIconColor: this.iconColourEnum.yellow,
        contextHeading: 'Please provide a layout name',
        contextDescription: ['Enter a name for your layout to continue.'],
      };

      const modal = await this.modalController.create({
        component: SharedModalBasicComponent,
        componentProps,
        cssClass: ['shared-basic-modal-css-small'],
      });
      await modal.present();
      return;
    }
    this.saveLayout.emit(this.newLayoutName);
  }

  async confirmLoad(layoutName: string): Promise<void> {
    const componentProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Load',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Load Layout',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: this.iconColourEnum.yellow,
      contextHeading: 'Load selected layout',
      contextDescription: [
        `Are you sure you want to load "${layoutName}"?`,
        'This will replace your current design.',
      ],
    };

    const modal = await this.modalController.create({
      component: SharedModalBasicComponent,
      componentProps,
      cssClass: ['shared-basic-modal-css-small'],
    });
    await modal.present();

    const {data} = await modal.onDidDismiss();
    if (data?.buttonPressed === 'button-accept') {
      this.loadLayout.emit(layoutName);
    }
  }

  selectExistingLayout(layoutName: string): void {
    this.newLayoutName = layoutName;
    this.selected = true;
  }

  isLayoutSelectedForCopy(layoutName: string): boolean {
    return this.selectedForCopy.has(layoutName);
  }

  onCopyCheckboxClick(layoutName: string, event: Event): void {
    event.stopPropagation();
    if (this.selectedForCopy.has(layoutName)) {
      this.selectedForCopy.delete(layoutName);
    } else {
      this.selectedForCopy.add(layoutName);
    }
    this.selectedForCopy = new Set(this.selectedForCopy);
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedForCopy = new Set();
      return;
    }
    this.selectedForCopy = new Set(this.savedLayouts.map((layout: ILayout) => layout.name));
  }

  onCopyLayouts(): void {
    if (!this.canCopy) {
      return;
    }
    this.copyLayouts.emit([...this.selectedForCopy]);
  }

  onCopySingleLayout(layoutName: string, event: Event): void {
    event.stopPropagation();
    if (!this.allowCopy) {
      return;
    }
    this.copyLayouts.emit([layoutName]);
  }

  onPasteLayouts(): void {
    if (!this.hasClipboardLayouts || this.isPasting) {
      return;
    }
    this.pasteLayouts.emit();
  }

  onDeleteLayout(layoutName: string, event: Event): void {
    this.deleteLayout.emit({name: layoutName, event});
  }

  closeDialog(): void {
    this.dialogClosed.emit();
  }
}
